/* Change all pairs in one ordinary cart row in the EGO cart drawer. */
(() => {
  if (window.__upliefEgoVariantEditor) return;
  window.__upliefEgoVariantEditor = true;
  const base = window.Shopify?.routes?.root || '/';
  const cache = new Map();
  let observed, observer, queued = false, rendering = false, rerender = false, busy = false;
  const root = () => document.querySelector('#opus-shadow-container')?.shadowRoot || document.querySelector('#opus-drawer-id');
  async function json(url, init) {
    const response = await fetch(url, init);
    let data;
    try { data = await response.json(); } catch {}
    if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Unable to update your cart. Please try again.');
    return data;
  }
  const cart = () => json(base + 'cart.js', {cache:'no-store'});
  const post = (path, body) => json(base + 'cart/' + path + '.js', {
    method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(body)
  });
  const same = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});
  const gift = item => item.final_line_price === 0 || Object.keys(item.properties || {}).some(key => key.includes('free_gift'));
  const addon = item => !!(item.properties?._amp_u || item.properties?._uplief_addon_source);
  function product(handle) {
    if (!cache.has(handle)) cache.set(handle, json(base + 'products/' + encodeURIComponent(handle) + '.js').catch(error => {cache.delete(handle); throw error;}));
    return cache.get(handle);
  }
  const matching = (items, old) => items.find(item => String(item.variant_id) === String(old.variant_id) && same(item.properties, old.properties));
  function details(row) {
    const href = row.querySelector('a.cd-product-item-title')?.getAttribute('href');
    if (!href) return null;
    try {
      const url = new URL(href, location.origin);
      return {id:url.searchParams.get('variant'), handle:url.pathname.split('/products/')[1]?.split('/')[0]};
    } catch { return null; }
  }
  function message(row, value) {
    let node = row.querySelector('.uplief-variant-error');
    if (!node) {
      node = document.createElement('span'); node.className = 'uplief-variant-error';
      node.setAttribute('role','alert'); row.querySelector('.uplief-variant-editor')?.append(node);
    }
    if (node) node.textContent = value;
  }
  async function refresh() {
    if (typeof window.opusRefreshCart === 'function') await window.opusRefreshCart();
    else window.dispatchEvent(new Event('ocu:cart:open:updated'));
    schedule();
  }
  async function change(row, old, next, control, restore) {
    if (busy || String(old.variant_id) === String(next)) return;
    busy = true; control.disabled = true; row.setAttribute('aria-busy','true');
    let added = false, changed = false, quantity = 0;
    try {
      const before = await cart();
      const current = matching(before.items, old);
      if (!current || gift(current) || current.quantity < 1) throw new Error('This item changed. Please reopen the cart and try again.');
      quantity = current.quantity;
      const info = details(row);
      const data = await product(info.handle);
      const selectedVariant = data.variants.find(v => String(v.id) === String(next) && v.available);
      if (!selectedVariant) throw new Error('That option is unavailable. Please choose another.');
      const entry = {id:Number(next), quantity};
      if (Object.keys(current.properties || {}).length) entry.properties = current.properties;
      if (current.selling_plan_allocation?.selling_plan?.id) entry.selling_plan = current.selling_plan_allocation.selling_plan.id;
      await post('add', {items:[entry]}); added = true;
      const after = await cart();
      if (addon(current)) {
        const replacement = after.items.find(item => String(item.variant_id) === String(next) && same(item.properties, current.properties));
        if (!replacement || replacement.final_line_price > Math.ceil(Number(selectedVariant.price) * replacement.quantity / 2)) {
          throw new Error('The 50% add-on price did not transfer. Your original option is still in the cart.');
        }
      }
      const original = matching(after.items, current);
      if (!original) throw new Error('The original pair could not be found after adding the new option.');
      await post('change', {id:original.key, quantity:0});
      added = false; changed = true; message(row, '');
      await refresh();
    } catch (error) {
      if (added && !changed) {
        try {
          const after = await cart();
          const extra = after.items.find(item => String(item.variant_id) === String(next) && same(item.properties, old.properties));
          if (extra) await post('change', {id:extra.key, quantity:Math.max(0, extra.quantity - quantity)});
        } catch {}
      }
      if (!changed) restore();
      message(row, changed ? 'Options changed. Please reopen the cart to see the updated total.' : error.message || 'Unable to change these options. Please try again.');
      if (added || changed) await refresh().catch(() => {});
    } finally {
      busy = false; row.removeAttribute('aria-busy'); control.disabled = false; schedule();
    }
  }
  async function render() {
    if (rendering) { rerender = true; return; }
    rendering = true;
    try {
    const drawer = root();
    if (!drawer || busy || !drawer.querySelector('.opus-drawer-container.open')) return;
    const rows = [...drawer.querySelectorAll('.cd-product-item:not(.cd-product-item-free-gift)')];
    const pending = rows.filter(row => !row.querySelector('.uplief-variant-editor') && details(row)?.id);
    if (!pending.length) return;
    const handles = [...new Set(pending.map(row => details(row)?.handle).filter(Boolean))];
    let snapshot;
    try {
      [snapshot] = await Promise.all([cart(), ...handles.map(handle => product(handle).catch(() => null))]);
    } catch { return; }
    for (const row of pending) {
      if (!row.isConnected || row.querySelector('.uplief-variant-editor')) continue;
      const info = details(row);
      if (!info?.id || !info.handle) continue;
      const matches = snapshot.items.filter(item => String(item.variant_id) === info.id);
      if (matches.length !== 1 || gift(matches[0])) continue;
      let data;
      try { data = await product(info.handle); } catch { continue; }
      if (!row.isConnected || row.querySelector('.uplief-variant-editor')) continue;
      const variants = data.variants.filter(v => v.available || String(v.id) === info.id);
      if (variants.length < 2) continue;
      const mount = row.querySelector('.cd-product-item-title-container');
      if (!mount) continue;
      const wrapper = document.createElement('div'); wrapper.className = 'uplief-variant-editor';
      const trigger = document.createElement('button'); trigger.type = 'button'; trigger.className = 'uplief-variant-trigger';
      trigger.setAttribute('aria-label','Change options for all ' + data.title + ' in this cart row');
      trigger.setAttribute('aria-expanded','false');
      const menu = document.createElement('div'); menu.className = 'uplief-variant-menu'; menu.hidden = true;
      for (const variant of variants) {
        const choice = document.createElement('button'); choice.type = 'button'; choice.className = 'uplief-variant-choice';
        choice.textContent = variant.public_title || variant.title;
        choice.setAttribute('aria-current', String(variant.id) === info.id ? 'true' : 'false');
        if (String(variant.id) === info.id) trigger.textContent = choice.textContent;
        choice.addEventListener('click', event => {
          event.stopPropagation(); menu.hidden = true; trigger.setAttribute('aria-expanded','false');
          const previous = trigger.textContent; trigger.textContent = choice.textContent;
          void change(row, matches[0], variant.id, trigger, () => { trigger.textContent = previous; });
        });
        menu.append(choice);
      }
      trigger.addEventListener('click', event => {
        event.stopPropagation(); if (busy) return; menu.hidden = !menu.hidden;
        trigger.setAttribute('aria-expanded', String(!menu.hidden));
      });
      wrapper.append(trigger, menu); mount.append(wrapper);
    }
    } finally {
      rendering = false;
      if (rerender) { rerender = false; schedule(); }
    }
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {queued = false; void render();});
  }
  let priceResetTimer;
  function maskPrices(event) {
    const button = event.target.closest?.('.cd-number-input-button');
    if (!button || !button.closest('.cd-product-item')) return;
    const drawer = root()?.querySelector('.opus-drawer-container');
    if (!drawer) return;
    drawer.classList.add('uplief-price-recalculating');
    clearTimeout(priceResetTimer);
    const began = Date.now();
    let stableAt = began, last = '';
    const settle = () => {
      if (!drawer.isConnected) return;
      const waiting = !!window.__upliefCartRequests?.pending ||
        !!drawer.querySelector('.cd-number-input-button[aria-busy="true"], .cd-product-item[aria-busy="true"], .cd-modal-content [aria-busy="true"], .cd-loading');
      const prices = [...drawer.querySelectorAll('.opus-final-price, .opus-subtotal-amount')].map(node => node.textContent).join('|');
      if (waiting || prices !== last) { last = prices; stableAt = Date.now(); }
      if ((!waiting && Date.now() - began > 450 && Date.now() - stableAt > 250) || Date.now() - began > 15000) {
        drawer.classList.remove('uplief-price-recalculating');
        return;
      }
      priceResetTimer = setTimeout(settle, 80);
    };
    priceResetTimer = setTimeout(settle, 80);
  }
  function attach() {
    const next = root();
    if (!next || next === observed) return;
    observer?.disconnect(); if (observed) observed.removeEventListener('click', maskPrices, true); observed = next;
    next.addEventListener('click', maskPrices, true);
    const style = document.createElement('style');
    style.textContent = '.uplief-variant-editor{display:block;margin-top:8px;font-size:13px;line-height:1.35}.uplief-variant-trigger,.uplief-variant-choice{box-sizing:border-box;min-height:40px;padding:7px 10px;border:1px solid #b9b9b9!important;border-radius:6px;background:#fff;color:#222;font:inherit;text-align:left;touch-action:manipulation;-webkit-tap-highlight-color:transparent}.uplief-variant-trigger{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;max-width:270px}.uplief-variant-trigger:after{content:"⌄";font-size:20px;line-height:1}.uplief-variant-menu{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;max-width:270px;margin-top:6px}.uplief-variant-menu[hidden]{display:none}.uplief-variant-choice[aria-current=true]{border-color:#ed5b00!important;background:#fff1e8}.uplief-variant-trigger:focus-visible,.uplief-variant-choice:focus-visible{outline:2px solid #ed5b00;outline-offset:2px}.uplief-variant-error{display:block;margin-top:4px;color:#a52424;font-size:12px}.uplief-variant-error:empty{display:none}.uplief-price-recalculating .opus-final-price,.uplief-price-recalculating .opus-subtotal-amount,.uplief-price-recalculating .cd-checkout-section-discount-amount{visibility:hidden!important}.uplief-price-recalculating .opus-price-display:after{content:"•••";color:#ed5b00;font-weight:700}@media(max-width:600px){.uplief-variant-trigger,.uplief-variant-menu{max-width:100%}.uplief-variant-trigger,.uplief-variant-choice{font-size:14px}}';
    (next instanceof ShadowRoot ? next : document.head).append(style);
    observer = new MutationObserver(records => {
      if (records.some(record =>
        (record.type === 'attributes' && record.target.matches?.('.opus-drawer-container')) ||
        [...record.addedNodes].some(node =>
          node.nodeType === 1 && (node.matches?.('.cd-product-item') || node.querySelector?.('.cd-product-item'))
        )
      )) schedule();
    });
    observer.observe(next,{childList:true,attributes:true,attributeFilter:['class'],subtree:true}); schedule();
  }
  const timer = setInterval(attach, 500);
  window.addEventListener('pagehide', () => clearInterval(timer), {once:true});
  attach();
})();